import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, FolderOpen, Search, User, Calendar, ChevronRight, Users,
} from "lucide-react";

/**
 * Cartella Paziente — mostra tutti i pazienti del medico corrente aggregati per
 * Codice Fiscale, con numero di visite e possibilità di aprire la cartella
 * dettagliata (storico visite, PDF, dati antropometrici principali).
 */
export default function CartellaPazientiPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [query, setQuery] = useState("");

  const loadPatients = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/patients/folders");
      setPatients(data.items || []);
    } catch (e) {
      toast.error("Impossibile caricare le cartelle pazienti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPatients(); }, []);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) => {
      const name = `${p.patient_first_name || ""} ${p.patient_last_name || ""}`.toLowerCase();
      const cf = (p.patient_fiscal_code || "").toLowerCase();
      const email = (p.patient_email || "").toLowerCase();
      return name.includes(q) || cf.includes(q) || email.includes(q);
    });
  }, [patients, query]);

  const openFolder = (p) => {
    if (p.patient_fiscal_code) navigate(`/cartella-paziente/${encodeURIComponent(p.patient_fiscal_code)}`);
    else navigate(`/cartella-paziente/_?email=${encodeURIComponent(p.patient_email || "")}`);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "—"; }
  };


  // ============================================================
  // Elenco pazienti (lista aggregata per CF)
  // ============================================================
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate("/dashboard")}
                  data-testid="cartella-back-btn" className="h-10">
            <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
          </Button>
          <div className="flex-1 min-w-[220px] max-w-md ml-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                     placeholder="Cerca paziente per nome, CF o email…"
                     data-testid="cartella-search"
                     className="pl-9 h-10" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Users className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Cartelle pazienti</h1>
            <p className="text-sm text-slate-500">Storico visite raggruppato per Codice Fiscale.</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-16">Caricamento cartelle…</div>
        ) : filtered.length === 0 ? (
          <Card className="p-10 rounded-2xl border-slate-200 bg-white text-center">
            <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <div className="text-slate-500">
              {query ? "Nessun paziente corrisponde alla ricerca." : "Ancora nessuna cartella. Le visite completate verranno archiviate qui."}
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((p, idx) => (
              <Card key={p.patient_fiscal_code || p.patient_email || idx}
                    role="button" tabIndex={0}
                    onClick={() => openFolder(p)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openFolder(p); }}
                    data-testid={`patient-folder-${idx}`}
                    className="group p-4 rounded-2xl border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-emerald-400 cursor-pointer transition">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-emerald-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">
                      {p.patient_first_name} {p.patient_last_name}
                    </div>
                    {p.patient_fiscal_code && (
                      <div className="mt-0.5 text-[11px] text-slate-500 font-mono truncate">
                        {p.patient_fiscal_code}
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200">
                        {p.visits_count} {p.visits_count === 1 ? "visita" : "visite"}
                      </Badge>
                      {p.cases_count > 0 && (
                        <Badge className="bg-blue-50 text-blue-800 border-blue-200">{p.cases_count} casi</Badge>
                      )}
                      {p.reports_count > 0 && (
                        <Badge className="bg-slate-50 text-slate-700 border-slate-200">{p.reports_count} referti</Badge>
                      )}
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> Ultima: {fmtDate(p.last_visit_at)}
                      </span>
                    </div>
                    {p.last_visit_reason && (
                      <div className="mt-1 text-xs text-slate-600 truncate">
                        {p.last_visit_reason}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-600 transition flex-shrink-0" />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
